import { memo } from "react";
import { Ban, RefreshCw, ExternalLink } from "lucide-react";
import { TableCell, TableRow } from "@/shared/components/ui/table";
import { Button } from "@/shared/components/ui/button";
import { Spinner } from "@/shared/components/ui/spinner";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";
import {
  formatCurrency,
  formatDateTime,
  formatDocument,
} from "@/shared/lib/formatters";
import { CommercialInvoiceDownloadActions } from "./CommercialInvoiceDownloadActions";
import {
  COMMERCIAL_INVOICE_PAYMENT_METHOD_LABELS,
  CommercialInvoiceStatusBadge,
} from "./CommercialInvoiceStatusBadge";
import { getCancellationEligibility } from "../utils/cancellation";
import { parseSefazMessage } from "@/shared/lib/sefaz-message";
import type { CommercialInvoiceResponseDTO } from "../types/commercial-invoice.types";

const NFE_KEY_VISIBLE_DIGITS = 12;

interface CommercialInvoiceRowProps {
  invoice: CommercialInvoiceResponseDTO;
  now: number;
  refreshing: boolean;
  onRefresh: (invoice: CommercialInvoiceResponseDTO) => void;
  onCancel: (invoice: CommercialInvoiceResponseDTO) => void;
}

export const CommercialInvoiceRow = memo(function CommercialInvoiceRow({
  invoice,
  now,
  refreshing,
  onRefresh,
  onCancel,
}: CommercialInvoiceRowProps) {
  const cancellation = getCancellationEligibility(invoice, now);
  const canRefresh = invoice.status !== "CANCELED";
  const sefazMessage = parseSefazMessage(invoice.sefazMessage);
  const sefazCode = invoice.sefazStatus ?? sefazMessage?.code ?? null;

  const buyerLabel =
    invoice.buyerName ??
    (invoice.buyerDocument
      ? formatDocument(invoice.buyerDocument)
      : "Sem identificação");

  return (
    <TableRow>
      <TableCell className="py-3 align-top font-medium">{invoice.id}</TableCell>

      <TableCell className="py-3 align-top">
        <div className="flex flex-col">
          <span
            className={invoice.buyerName ? undefined : "text-muted-foreground"}
          >
            {buyerLabel}
          </span>
          {invoice.buyerName && invoice.buyerDocument && (
            <span className="text-xs text-muted-foreground">
              {formatDocument(invoice.buyerDocument)}
            </span>
          )}
          {invoice.clientId !== null && (
            <span className="text-xs text-muted-foreground">
              Cliente cadastrado #{invoice.clientId}
            </span>
          )}
        </div>
      </TableCell>

      <TableCell className="py-3 align-top">
        <div className="flex flex-col gap-1">
          <CommercialInvoiceStatusBadge status={invoice.status} />
          {invoice.status === "ERROR" && (
            <span className="text-xs text-destructive max-w-xs">
              {sefazMessage?.message ??
                "A SEFAZ recusou a nota. Confira os dados e emita novamente."}
              {sefazCode ? ` (código ${sefazCode})` : ""}
            </span>
          )}
          {invoice.status === "PENDING" && (
            <span className="text-xs text-muted-foreground max-w-xs">
              Ainda sem confirmação. Use Reconsultar para saber o resultado.
            </span>
          )}
          {invoice.status === "CANCELED" && invoice.cancellationReason && (
            <span
              className="text-xs text-muted-foreground max-w-xs truncate"
              title={invoice.cancellationReason}
            >
              {invoice.cancellationReason}
            </span>
          )}
        </div>
      </TableCell>

      <TableCell className="py-3 align-top">
        {COMMERCIAL_INVOICE_PAYMENT_METHOD_LABELS[invoice.paymentMethod]}
      </TableCell>

      <TableCell className="py-3 pr-6 text-right align-top tabular-nums">
        {formatCurrency(invoice.value)}
      </TableCell>

      <TableCell className="py-3 align-top">
        {invoice.invoiceNumber ? (
          <div className="flex flex-col">
            <span>
              Nº {invoice.invoiceNumber}
              {invoice.invoiceSeries ? ` · série ${invoice.invoiceSeries}` : ""}
            </span>
            {invoice.nfeKey && (
              <span
                className="text-xs text-muted-foreground font-mono"
                title={invoice.nfeKey}
              >
                …{invoice.nfeKey.slice(-NFE_KEY_VISIBLE_DIGITS)}
              </span>
            )}
            {invoice.consultUrl && (
              <a
                href={invoice.consultUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              >
                Consultar na SEFAZ
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>

      <TableCell className="py-3 align-top">
        <div className="flex flex-col">
          <span>{formatDateTime(invoice.createdAt)}</span>
          {invoice.issuedAt && (
            <span className="text-xs text-muted-foreground">
              Autorizada: {formatDateTime(invoice.issuedAt)}
            </span>
          )}
        </div>
      </TableCell>

      <TableCell className="py-3 align-top">
        <CommercialInvoiceDownloadActions invoice={invoice} />
      </TableCell>

      <TableCell className="py-3 text-right align-top">
        <div className="flex justify-end gap-1">
          <span
            className="inline-flex"
            title={
              canRefresh
                ? "Reconsultar situação da nota"
                : "Nota cancelada não precisa de reconsulta"
            }
          >
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onRefresh(invoice)}
              aria-label="Reconsultar situação da nota comercial"
              disabled={!canRefresh || refreshing}
            >
              {refreshing ? (
                <Spinner className="w-4 h-4" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          </span>

          <Can permission={ACTIONS.commercialInvoices.cancel}>
            <span className="inline-flex" title={cancellation.reason}>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => onCancel(invoice)}
                aria-label={
                  cancellation.allowed
                    ? "Cancelar nota comercial"
                    : `Cancelar nota comercial indisponível: ${cancellation.reason}`
                }
                disabled={!cancellation.allowed || refreshing}
              >
                <Ban className="w-4 h-4 text-amber-600" />
              </Button>
            </span>
          </Can>
        </div>
      </TableCell>
    </TableRow>
  );
});
